/**
 * Shared Embla Carousel initializer.
 *
 * Sections opt-in by rendering this markup:
 *
 *   <div data-embla data-embla-options='{"loop":true,"align":"start"}' data-embla-autoplay="5000">
 *     <div class="embla__viewport">
 *       <div class="embla__container">
 *         <div class="embla__slide">...</div>
 *         <div class="embla__slide">...</div>
 *       </div>
 *     </div>
 *   </div>
 *
 * Optional controls (placed anywhere inside the [data-embla] root):
 *
 *   <button data-embla-prev>Prev</button>
 *   <button data-embla-next>Next</button>
 *   <div data-embla-dots></div>   <!-- one button per slide is auto-generated -->
 *   <span data-embla-current></span> / <span data-embla-total></span>
 *
 * Options recognised on the root element:
 *   - data-embla-options : JSON string passed straight to EmblaCarousel(options).
 *   - data-embla-autoplay : ms delay. Adds the Autoplay plugin when present.
 *   - data-embla-class-names : truthy -> adds ClassNames plugin (toggles .is-selected on slides).
 *   - data-embla-dot-class : class applied to each auto-generated dot button (default "embla__dot").
 */
(function () {
  function ready(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  function parseJSON(s, fallback) {
    if (!s) return fallback;
    try {
      return JSON.parse(s);
    } catch (e) {
      return fallback;
    }
  }

  function initOne(root) {
    if (!window.EmblaCarousel) return;
    if (root.__embla) return;

    var viewport = root.querySelector('.embla__viewport');
    if (!viewport) return;

    var options = parseJSON(root.getAttribute('data-embla-options'), {});
    var plugins = [];

    /* Video slides drive their own timing (see syncActiveSlide below), so the
     * autoplay pointer handlers must not pause/restart on hover — otherwise
     * moving onto an overlapping control (e.g. the arrows, which sit outside
     * the viewport) reads as a mouseleave and resets the progress bar. */
    var containsVideo = !!viewport.querySelector('video');

    var autoplayDelay = parseInt(root.getAttribute('data-embla-autoplay'), 10);
    if (autoplayDelay && window.EmblaCarouselAutoplay) {
      plugins.push(window.EmblaCarouselAutoplay({
        delay: autoplayDelay,
        stopOnInteraction: false,
        stopOnMouseEnter: !containsVideo,
      }));
    }

    if (root.hasAttribute('data-embla-class-names') && window.EmblaCarouselClassNames) {
      plugins.push(window.EmblaCarouselClassNames());
    }

    var embla = window.EmblaCarousel(viewport, options, plugins);
    root.__embla = embla;

    /* Controls scope: nearest .shopify-section ancestor (or root itself if controls live inside).
     * This lets sections place prev/next/dots in the header above the viewport. */
    var scope = root.closest('.shopify-section') || root;

    /* Prev / Next */
    var prev = scope.querySelector('[data-embla-prev]');
    var next = scope.querySelector('[data-embla-next]');
    if (prev) prev.addEventListener('click', function () { embla.scrollPrev(); restartProgress(); });
    if (next) next.addEventListener('click', function () { embla.scrollNext(); restartProgress(); });

    function updateNavState() {
      if (prev) prev.disabled = !embla.canScrollPrev();
      if (next) next.disabled = !embla.canScrollNext();
    }

    /* Dots */
    var dotsContainer = scope.querySelector('[data-embla-dots]');
    var dotClass = root.getAttribute('data-embla-dot-class') || 'embla__dot';
    var dotNodes = [];

    function buildDots() {
      if (!dotsContainer) return;
      dotsContainer.innerHTML = '';
      dotNodes = embla.scrollSnapList().map(function (_, i) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = dotClass;
        btn.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        btn.addEventListener('click', function () { embla.scrollTo(i); restartProgress(); });
        dotsContainer.appendChild(btn);
        return btn;
      });
      updateDots();
    }

    function updateDots() {
      var selected = embla.selectedScrollSnap();
      dotNodes.forEach(function (n, i) {
        if (i === selected) {
          n.classList.add('is-active');
          n.setAttribute('aria-current', 'true');
        } else {
          n.classList.remove('is-active');
          n.removeAttribute('aria-current');
        }
      });
    }

    /* Counter (current/total) */
    var currentEl = scope.querySelector('[data-embla-current]');
    var totalEl = scope.querySelector('[data-embla-total]');

    function pad(n) { return n < 10 ? '0' + n : String(n); }

    function updateCounter() {
      var snaps = embla.scrollSnapList();
      var idx = embla.selectedScrollSnap();
      if (currentEl) currentEl.textContent = pad(idx + 1);
      if (totalEl) totalEl.textContent = pad(snaps.length);
    }

    /* Segmented pagination (optional [data-embla-segments] container).
     * One segment per slide is auto-generated; the active segment's fill becomes
     * the progress target below, the rest stay empty. Clicking a segment jumps. */
    var segmentsContainer = scope.querySelector('[data-embla-segments]');
    var segmentClass = root.getAttribute('data-embla-segment-class') || 'embla__segment';
    var segmentFillClass = root.getAttribute('data-embla-segment-fill-class') || 'embla__segment-fill';
    var segFills = [];

    function buildSegments() {
      if (!segmentsContainer) return;
      segmentsContainer.innerHTML = '';
      segFills = embla.scrollSnapList().map(function (_, i) {
        var seg = document.createElement('button');
        seg.type = 'button';
        seg.className = segmentClass;
        seg.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        var fill = document.createElement('span');
        fill.className = segmentFillClass;
        seg.appendChild(fill);
        seg.addEventListener('click', function () { embla.scrollTo(i); restartProgress(); });
        segmentsContainer.appendChild(seg);
        return fill;
      });
      updateSegments();
    }

    function updateSegments() {
      if (!segFills.length) return;
      var selected = embla.selectedScrollSnap();
      segFills.forEach(function (f, i) {
        var seg = f.parentNode;
        var active = i === selected;
        seg.classList.toggle('is-active', active);
        if (active) seg.setAttribute('aria-current', 'true');
        else seg.removeAttribute('aria-current');
        /* Stories-style progress: past slides stay full, upcoming stay empty,
         * the active one starts empty and is animated by the progress logic. */
        f.style.transition = 'none';
        f.style.transform = i < selected ? 'scaleX(1)' : 'scaleX(0)';
      });
    }

    /* "Next" preview card (optional [data-embla-next-title] / [data-embla-next-thumb]). */
    var nextTitleEl = scope.querySelector('[data-embla-next-title]');
    var nextThumbEl = scope.querySelector('[data-embla-next-thumb]');

    function updateNextCard() {
      if (!nextTitleEl && !nextThumbEl) return;
      var nodes = embla.slideNodes();
      if (!nodes.length) return;
      var next = nodes[(embla.selectedScrollSnap() + 1) % nodes.length];
      if (!next) return;
      if (nextTitleEl) nextTitleEl.textContent = next.getAttribute('data-slide-title') || '';
      if (nextThumbEl) {
        var thumb = next.getAttribute('data-slide-thumb') || '';
        if (thumb && nextThumbEl.getAttribute('src') !== thumb) nextThumbEl.setAttribute('src', thumb);
      }
    }

    /* Progress fill + autoplay sync. The fill target is the active segment when
     * segments are used, otherwise a single [data-embla-progress] element.
     *
     * Three modes, chosen automatically:
     *   1. autoplay + slides contain <video>  -> each video slide plays in full
     *      (dynamic duration) before advancing, and the fill tracks the video's
     *      playback. Image slides use the fixed autoplay delay.
     *   2. autoplay, no video                 -> fill is a countdown over the delay.
     *   3. no autoplay                        -> fill mirrors the scroll position. */
    var progressEl = scope.querySelector('[data-embla-progress]');
    var autoplay = (typeof embla.plugins === 'function' && embla.plugins().autoplay) || null;
    var videoSync = !!(autoplay && containsVideo);

    function currentFill() {
      if (segFills.length) return segFills[embla.selectedScrollSnap()] || null;
      return progressEl;
    }

    function setFill(scale, durationMs) {
      var el = currentFill();
      if (!el) return;
      el.style.transition = durationMs ? 'transform ' + durationMs + 'ms linear' : 'none';
      el.style.transform = 'scaleX(' + scale + ')';
    }

    /* Freeze the fill at its current (mid-animation) position. */
    function freezeFill() {
      var el = currentFill();
      if (!el) return;
      var matrix = window.getComputedStyle(el).transform;
      el.style.transition = 'none';
      el.style.transform = matrix && matrix !== 'none' ? matrix : 'scaleX(0)';
    }

    /* Mode 2 — animate 0 -> 100% over the remaining autoplay delay. */
    function startTimerFill() {
      var el = currentFill();
      if (!el || !autoplay) return;
      var remaining = autoplay.timeUntilNext();
      if (remaining == null) return;
      setFill(0, 0);
      void el.offsetWidth; /* reflow so the next transition animates from 0 */
      setFill(1, remaining);
    }

    /* Mode 3 — mirror manual scroll position. */
    function updateScrollFill() {
      setFill(Math.max(0, Math.min(1, embla.scrollProgress())), 0);
    }

    /* Jumping by hand (segment, dot, prev/next) has to restart the fill.
     * The Autoplay plugin never listens to 'select' - it only re-arms its timer
     * when it advances by itself - so the old timer keeps running, no
     * 'autoplay:timerset' is emitted, and the newly selected segment sits at
     * scaleX(0) (where updateSegments left it) until the stale timer fires.
     * play() clears that timer, starts a fresh one and emits 'timerset', which
     * is what drives startTimerFill.
     *
     * Call this AFTER embla.scrollTo/scrollNext/scrollPrev: embla emits 'select'
     * synchronously from those, so the fill reset has already happened by the
     * time we get here. */
    function restartProgress() {
      /* No autoplay -> the fill mirrors scroll position, handled by 'scroll'. */
      if (!autoplay) return;
      /* Video slides drive their own fill from syncActiveSlide/runVideoFill,
       * and autoplay is deliberately stopped for them. */
      if (videoSync && videoFromSnap()) return;
      autoplay.play();
    }

    /* Mode 1 — video-synced autoplay. */
    var activeVideo = null;
    var detachVideo = function () {};

    /* Optional mute toggle ([data-embla-mute]). Videos start muted (autoplay);
     * the preference persists across slides via syncActiveSlide. */
    var muteBtn = scope.querySelector('[data-embla-mute]');
    var soundOn = false;

    function reflectMute() {
      root.classList.toggle('is-sound-on', soundOn);
      if (muteBtn) muteBtn.setAttribute('aria-label', soundOn ? 'Mute video' : 'Unmute video');
    }

    function videoFromSnap() {
      var nodes = embla.slideNodes();
      var slide = nodes[embla.selectedScrollSnap()];
      return slide ? slide.querySelector('video') : null;
    }

    /* Animate the fill from the video's current position to 100% over the time
     * left in the clip. Re-run on play/resume so it stays in sync after buffering. */
    function runVideoFill() {
      var el = currentFill();
      if (!el || !activeVideo) return;
      var d = activeVideo.duration;
      if (!d || !isFinite(d)) return;
      setFill(Math.min(1, activeVideo.currentTime / d), 0);
      void el.offsetWidth;
      setFill(1, Math.max(0, (d - activeVideo.currentTime) * 1000));
    }

    function syncActiveSlide() {
      if (!videoSync) return;
      detachVideo();
      detachVideo = function () {};

      var nodes = embla.slideNodes();
      var idx = embla.selectedScrollSnap();

      /* Pause + rewind every non-active video so each starts fresh when reached. */
      nodes.forEach(function (s, i) {
        var v = s.querySelector('video');
        if (v && i !== idx) { try { v.pause(); v.currentTime = 0; } catch (e) {} }
      });

      activeVideo = videoFromSnap();

      if (!activeVideo) {
        /* Image slide -> hand control back to the fixed-delay autoplay timer. */
        if (autoplay && (typeof autoplay.isPlaying !== 'function' || !autoplay.isPlaying())) {
          autoplay.play();
        }
        return;
      }

      /* Video slide -> advance is driven by the video, not the fixed delay. */
      var video = activeVideo;
      if (autoplay) autoplay.stop();
      video.loop = false; /* let it fire 'ended' so we can advance */
      video.muted = !soundOn; /* honour the user's mute choice across slides */
      setFill(0, 0);

      var onPlaying = function () { runVideoFill(); };
      var onDuration = function () { if (!video.paused) runVideoFill(); };
      var onHold = function () { freezeFill(); };
      var onEnded = function () { embla.scrollNext(); };
      var onError = function () { embla.scrollNext(); };

      video.addEventListener('playing', onPlaying);
      video.addEventListener('durationchange', onDuration);
      video.addEventListener('pause', onHold);
      video.addEventListener('waiting', onHold);
      video.addEventListener('ended', onEnded, { once: true });
      video.addEventListener('error', onError, { once: true });

      detachVideo = function () {
        video.removeEventListener('playing', onPlaying);
        video.removeEventListener('durationchange', onDuration);
        video.removeEventListener('pause', onHold);
        video.removeEventListener('waiting', onHold);
        video.removeEventListener('ended', onEnded);
        video.removeEventListener('error', onError);
      };

      try { video.currentTime = 0; } catch (e) {}
      var played = video.play();
      if (played && played.catch) {
        played.catch(function () {
          /* Autoplay blocked -> fall back to the fixed delay so we never stall. */
          detachVideo();
          detachVideo = function () {};
          activeVideo = null;
          if (autoplay) autoplay.play();
        });
      } else if (!video.paused && video.readyState >= 2) {
        runVideoFill();
      }
    }

    if (muteBtn) {
      muteBtn.addEventListener('click', function (e) {
        e.preventDefault();
        soundOn = !soundOn;
        var v = activeVideo || videoFromSnap();
        if (v) {
          v.muted = !soundOn;
          if (soundOn) { var p = v.play(); if (p && p.catch) p.catch(function () {}); }
        }
        reflectMute();
      });
      reflectMute();
    }

    embla.on('init', function () {
      buildDots();
      buildSegments();
      updateNavState();
      updateCounter();
      updateNextCard();
      if (videoSync) syncActiveSlide();
      else if (!autoplay) updateScrollFill();
    });
    embla.on('select', function () {
      updateDots();
      updateSegments();
      updateNavState();
      updateCounter();
      updateNextCard();
      if (videoSync) syncActiveSlide();
    });
    embla.on('reInit', function () {
      buildDots();
      buildSegments();
      updateNavState();
      updateCounter();
      updateNextCard();
      if (videoSync) syncActiveSlide();
      else if (!autoplay) updateScrollFill();
    });

    if (autoplay) {
      embla.on('autoplay:timerset', startTimerFill);
      embla.on('autoplay:timerstopped', freezeFill);
    } else {
      embla.on('scroll', updateScrollFill);
    }

    /* Initial sync (embla emits 'init' during creation, before the handler above
     * is attached, so run the same work manually here). */
    buildDots();
    buildSegments();
    updateNavState();
    updateCounter();
    updateNextCard();
    if (videoSync) {
      syncActiveSlide();
      /* First slide is an image -> kick the timer fill the missed 'init' would have. */
      if (!activeVideo && (typeof autoplay.isPlaying !== 'function' || autoplay.isPlaying())) {
        startTimerFill();
      }
    } else if (autoplay) {
      if (typeof autoplay.isPlaying !== 'function' || autoplay.isPlaying()) startTimerFill();
    } else {
      updateScrollFill();
    }
  }

  function initAll() {
    if (!window.EmblaCarousel) {
      /* Embla UMD might still be loading. Retry on next frame. */
      requestAnimationFrame(initAll);
      return;
    }
    document.querySelectorAll('[data-embla]').forEach(initOne);
  }

  ready(initAll);

  /* Re-init when Shopify theme editor adds/edits a section */
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target.querySelector('[data-embla]');
    if (el) initOne(el);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var el = e.target.querySelector('[data-embla]');
    if (el && el.__embla) {
      el.__embla.destroy();
      el.__embla = null;
    }
  });
})();
